// Pure tree-manipulation utilities used by PathCreationTableView.
// Ported from admin-v2/src/helpers/assignmentHelpers.js — keep logic intact, TypeScript only.

/**
 * Normalises a notifications-frequency value to lowercase for the Rails API.
 * Falls back to `fallback` (default: "none") when the value is null/undefined.
 */
export function normalizeNotificationsFrequency(
  value: string | null | undefined,
  fallback = "none"
): string {
  return typeof value === "string" ? value.toLowerCase() : fallback;
}

export interface SortableNode {
  key?: string | number | null;
  data: {
    name?: string;
    type?: string;
    topic?: string;
    score?: string | number;
    duration?: string | number;
    level?: string;
    [key: string]: unknown;
  };
  children?: SortableNode[];
}

export function sortTreeNodes(nodes: SortableNode[], field: string, order: number): SortableNode[] {
  if (!nodes || nodes.length === 0) return nodes;

  const getSortValue = (node: SortableNode, f: string): string | number => {
    const data = node.data;

    switch (f) {
      case "type":
        return (data.type as string) || "";
      case "topic":
        if (data.level === "topic") {
          return (data.name as string) || "";
        }
        return (data.topic as string) || "";
      case "score": {
        const scoreStr = data.score ?? "0";
        return parseInt(String(scoreStr).replace("%", ""), 10);
      }
      case "duration": {
        const durationStr = data.duration ?? "0";
        const match = String(durationStr).match(/(\d+)/);
        return match ? parseInt(match[1] ?? "0", 10) : 0;
      }
      case "name":
        return (data.name as string) || "";
      default:
        return (data[f] as string) || "";
    }
  };

  const getDescendantValues = (node: SortableNode, f: string): (string | number)[] => {
    const values: (string | number)[] = [];

    const collectValues = (currentNode: SortableNode) => {
      const value = getSortValue(currentNode, f);
      if (value && value !== "") {
        values.push(value);
      }
      currentNode.children?.forEach(collectValues);
    };

    collectValues(node);
    return values;
  };

  const shouldSortLevel = (ns: SortableNode[], f: string): boolean => {
    if (!ns.length) return false;

    const firstNodeLevel = ns[0]?.data?.level;

    switch (f) {
      case "topic":
        return firstNodeLevel === "topic" || firstNodeLevel === "lesson";
      case "type":
        return true;
      case "score":
        return firstNodeLevel === "topic" || firstNodeLevel === "lesson";
      case "duration":
        return true;
      case "name":
        return true;
      default:
        return ns.some(node => {
          const value = getSortValue(node, f);
          return value !== "" && value !== 0;
        });
    }
  };

  const getNodeSortValue = (node: SortableNode, f: string): string | number => {
    const directValue = getSortValue(node, f);

    if (f === "type" && (!directValue || directValue === "")) {
      const descendantValues = getDescendantValues(node, f);
      if (descendantValues.length > 0) {
        const typeCount: Record<string, number> = {};
        descendantValues.forEach(type => {
          typeCount[String(type)] = (typeCount[String(type)] ?? 0) + 1;
        });

        const sortedTypes = Object.entries(typeCount).sort((a, b) => {
          if (b[1] !== a[1]) return b[1] - a[1];
          return a[0].localeCompare(b[0]);
        });

        return sortedTypes[0]?.[0] ?? "";
      }
    }

    if (f === "duration" && (!directValue || directValue === 0)) {
      const descendantValues = getDescendantValues(node, f) as number[];
      if (descendantValues.length > 0) {
        return order === 1 ? Math.min(...descendantValues) : Math.max(...descendantValues);
      }
    }

    return directValue;
  };

  let sortedNodes = [...nodes];

  if (shouldSortLevel(nodes, field)) {
    sortedNodes = sortedNodes.sort((a, b) => {
      const aValue = getNodeSortValue(a, field);
      const bValue = getNodeSortValue(b, field);

      if ((!aValue || aValue === "") && (!bValue || bValue === "")) return 0;

      if (!aValue || aValue === "") return order === 1 ? 1 : -1;
      if (!bValue || bValue === "") return order === 1 ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      return order === 1 ? comparison : -comparison;
    });
  }

  return sortedNodes.map(node => {
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: sortTreeNodes(node.children, field, order),
      };
    }
    return node;
  });
}
